import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { LayoutDashboard, Users, ClipboardList, BookOpen, Package, LogOut, Menu, X, ChevronLeft, Shield, Trophy, BarChart3, Globe, CalendarDays, FileText, ImageIcon } from "lucide-react";
import { useState } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const a = t.admin;

  const navItems = [
    { label: a.dashboard, to: "/admin", icon: LayoutDashboard },
    { label: a.contentEditor || "Website Content", to: "/admin/content", icon: FileText },
    { label: a.galleryManager || "Gallery", to: "/admin/gallery", icon: ImageIcon },
    { label: a.events || "Events", to: "/admin/events", icon: CalendarDays },
    { label: a.athletes, to: "/admin/athletes", icon: Users },
    { label: a.teams, to: "/admin/teams", icon: Shield },
    { label: a.tournaments, to: "/admin/tournaments", icon: Trophy },
    { label: a.attendance, to: "/admin/attendance", icon: ClipboardList },
    { label: a.academics, to: "/admin/academics", icon: BookOpen },
    { label: a.gear, to: "/admin/gear", icon: Package },
    { label: a.analytics, to: "/admin/analytics", icon: BarChart3 },
  ];

  const isActive = (path) => location.pathname === path || (path !== "/admin" && location.pathname.startsWith(path));

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Curious 123" className="w-10 h-10 rounded-full" />
          <div>
            <h2 className="text-white font-bold text-sm font-['Kanit']">Curious 123</h2>
            <p className="text-gray-400 text-xs">{a.portal}</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map(item => (
          <Link key={item.to} to={item.to} onClick={() => setSidebarOpen(false)}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
              isActive(item.to) ? "bg-[#87CEEB]/15 text-[#87CEEB]" : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}>
            <item.icon className="w-5 h-5" />
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button onClick={toggle} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all w-full mb-1" data-testid="admin-lang-toggle">
          <Globe className="w-5 h-5" />
          {lang === "en" ? "Espanol" : "English"}
        </button>
        <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all mb-1">
          <ChevronLeft className="w-5 h-5" />
          {a.backToSite}
        </Link>
        <div className="px-4 py-2 mb-2">
          <p className="text-white text-sm font-medium truncate">{user?.name || "Admin"}</p>
          <p className="text-gray-500 text-xs truncate">{user?.email}</p>
        </div>
        <button onClick={handleLogout} data-testid="admin-logout"
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all">
          <LogOut className="w-5 h-5" />
          {a.signOut}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#f1f5f9]" data-testid="admin-layout">
      <aside className="hidden lg:flex lg:w-64 flex-col bg-[#0f172a] flex-shrink-0">
        <SidebarContent />
      </aside>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#0f172a] flex flex-col h-full">
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8 flex-shrink-0">
          <button className="lg:hidden p-2 hover:bg-gray-100 rounded-lg" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-gray-800 font-['Kanit'] hidden lg:block">
            {navItems.find(n => isActive(n.to))?.label || a.dashboard}
          </h1>
          <div className="text-sm text-gray-500">{a.welcome}, {user?.name || "Coach"}</div>
        </header>
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
